import type { DocumentLocation } from '~/components/Modals/MultipleLocationsModal/MultipleLocationsModal';
import type {
  HomeWorkDocumentItem,
  HomeWorkHandler,
} from '~/redux/api/methods/home/postHomeWork';

import type { DeadlineKind, WorkAction, WorkBucket, WorkItem } from '../types';

import { parseIsoDate } from '../effectiveDeadline';
import { workItemId } from '../workItemIds';

const BY_WORK_TYPE = {
  renewal: {
    deadlineKind: 'expiration',
    primaryType: 'renew',
  },
  auto_renewal: {
    deadlineKind: 'renewal_notice',
    primaryType: 'send_non_renewal_notice',
  },
} as const satisfies Record<
  HomeWorkDocumentItem['work_type'],
  { deadlineKind: DeadlineKind; primaryType: WorkAction['type'] }
>;

function toDocumentLocation(handler: HomeWorkHandler): DocumentLocation {
  return {
    id: handler.id,
    document_name: handler.name,
    // Path is absent in the payload - `useFolderPaths` resolves it when the modal opens.
    path: [],
    file_type: handler.file_type,
  };
}

export function mapDocumentItem(
  item: HomeWorkDocumentItem,
  lane: WorkBucket,
): WorkItem | null {
  const deadline = parseIsoDate(item.action_date);
  if (!deadline) return null;

  const primaryHandler = item.document_handlers[0];
  if (!primaryHandler) return null;

  // The payload is typed, not validated, so a work_type the frontend doesn't
  // know about would otherwise destructure undefined and take out the lane.
  const byWorkType = BY_WORK_TYPE[item.work_type];
  if (!byWorkType) return null;

  const { deadlineKind, primaryType } = byWorkType;
  const href = `/document/${primaryHandler.id}`;

  return {
    id: workItemId('document', item.document_id),
    kind: 'document',
    title: primaryHandler.name,
    typeLabel: item.contract_type ?? '',
    ...(item.counterparties.length > 0
      ? { counterparties: item.counterparties }
      : {}),
    deadline,
    bucket: lane,
    deadlineKind,
    primaryAction: { type: primaryType, href },
    secondaryActions: [{ type: 'terminate', href }],
    href,
    fileType: primaryHandler.file_type,
    ...(item.document_handlers.length > 1
      ? { locations: item.document_handlers.map(toDocumentLocation) }
      : {}),
  };
}
